import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { findSuggest } from '../../api/search-service/searchService';
import Logger from '../../utils/logger';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import SearchBarStyle from'../../assets/stylesheets/SearchBar.module.css';
import { ReactComponent as Close} from '../../assets/icons/close.svg';
import { ReactComponent as Back} from '../../assets/icons/back.svg'; 

const log = Logger.create('SearchBar');

const SearchBar = ({ onSearch, placeholder = "Search farms", className = "", onMobileSearchClose }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false); // Initialize as false to prevent flash
    const searchInputRef = useRef(null);
    const searchBarRef = useRef(null);
    const suggestionTimeoutRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    // Handle search execution
    const handleSearch = (e) => {
        e.preventDefault();        
        if (query.trim()) {
            setShowSuggestions(false);
            
            // Close mobile search modal if it exists
            if (isMobile && onMobileSearchClose) {
                onMobileSearchClose();
            }
            
            // If onSearch prop is provided, use it (for internal page search)
            if (onSearch) {
                onSearch(query.trim());
            } else {
                const searchUrl = `/search?q=${encodeURIComponent(query.trim())}`;
                navigate(searchUrl);
            }
        } else {
            log.warn('Empty query, search not executed');
        }
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        setQuery(suggestion);
        setShowSuggestions(false);
        setSuggestions([]); 
        
        if (isMobile) {
            // Mobile: immediately execute search
            // Close mobile search modal if it exists
            if (onMobileSearchClose) {
                onMobileSearchClose();
            }
            
            // If onSearch prop is provided, use it (for internal page search)
            if (onSearch) {
                onSearch(suggestion.trim());
            } else {
                const searchUrl = `/search?q=${encodeURIComponent(suggestion.trim())}`;
                navigate(searchUrl);
            }
        } else {
            // Desktop: just set the query and focus input
            searchInputRef.current?.focus();
        }
    };

    // Initialize mobile detection and handle resize
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            
            if (mobile) {
                setShowSuggestions(true);
                // Set initial position
                setTimeout(() => {
                    updateDropdownPosition();
                }, 100);
            }
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        const handleResize = () => {
            checkMobile();
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Handle input change with debouncing
    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);

        // Clear previous timeout
        if (suggestionTimeoutRef.current) {
            clearTimeout(suggestionTimeoutRef.current);
        }

        // Check if we're in mobile mode
        if (isMobile) {
            setShowSuggestions(true);
        } else if (value.trim()) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }

        // Set new timeout for suggestion fetch
        if (value.trim()) {
            // Show loading immediately when user starts typing
            setLoading(true);
            suggestionTimeoutRef.current = setTimeout(() => {
                fetchSuggestions(value);
            }, 300); // 300ms debounce
        } else {
            // Clear suggestions when input is empty
            setSuggestions([]);
            setLoading(false);
        }
    };

    // Debounced suggestion fetch
    const fetchSuggestions = async (searchQuery) => {
        if (!searchQuery.trim() || searchQuery.length < 1) {
            setSuggestions([]);
            setLoading(false);
            return;
        }

        // Don't set loading here as it's already set in handleInputChange
        try {
            const response = await findSuggest({ q: searchQuery });
            const suggestionItems = response.items || [];
            setSuggestions(suggestionItems);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate dropdown position
    const updateDropdownPosition = () => {
        if (searchBarRef.current) {
            const rect = searchBarRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                left: rect.left,
                width: rect.width
            });
        }
    };

    // Update position when showing suggestions
    useEffect(() => {
        if (showSuggestions && searchBarRef.current) {
            updateDropdownPosition();
            
            const handleResize = () => {
                updateDropdownPosition();
            };
            
            const handleScroll = () => {
                updateDropdownPosition();
            };
            
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);
            
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [showSuggestions]);

    // Handle outside clicks only
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdownElement = document.querySelector('[data-suggestions-dropdown]');
            
            if (searchBarRef.current && 
                !searchBarRef.current.contains(event.target) &&
                !dropdownElement?.contains(event.target)) {
                // Don't hide suggestions on mobile
                if (!isMobile) {
                    setShowSuggestions(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (suggestionTimeoutRef.current) {
                clearTimeout(suggestionTimeoutRef.current);
            }
        };
    }, [isMobile]);

    // Show suggestions on focus for mobile
    const handleInputFocus = () => {
        updateDropdownPosition();
        
        // Always show on mobile
        if (isMobile) {
            setShowSuggestions(true);
        } else if (query.trim()) {
            setShowSuggestions(true);
        }
    };

    // Handle clear input
    const handleClearInput = () => {
        setQuery('');
        setSuggestions([]);
        setLoading(false);
        searchInputRef.current?.focus();
    };

    // Handle back input - close mobile search modal or navigate back
    const handleBackInput = () => {
        // On mobile, if we have a close function, use it to close the modal
        if (isMobile && onMobileSearchClose) {
            onMobileSearchClose();
        } else {
            // Otherwise, use browser's back navigation
            navigate(-1);
        }
    };

    // Suggestions dropdown component
    const SuggestionsDropdown = () => {
        // Always show on mobile when showSuggestions is true
        if (!showSuggestions) return null;
        
        return createPortal(
            <div 
                className={SearchBarStyle["suggestions-dropdown"]}
                data-suggestions-dropdown
                style={{
                    position: 'fixed',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    zIndex: 10000
                }}
            >
                {/* Empty state when no query */}
                {!query.trim() && !loading && (
                    <div className={SearchBarStyle["empty-state"]}>
                        <div className={SearchBarStyle["empty-state-text"]}>
                            Start typing to search for farms
                        </div>
                    </div>
                )}
                
                {/* Loading state */}
                {loading && (
                    <div className={SearchBarStyle["loading-container"]}>
                        <div className={SearchBarStyle["search-loading-dots"]}>
                            <span className={SearchBarStyle["search-dot"]}></span>
                            <span className={SearchBarStyle["search-dot"]}></span>
                            <span className={SearchBarStyle["search-dot"]}></span>
                        </div>
                    </div>
                )}
                
                {/* Results or no results */}
                {!loading && query.trim() && (
                    <>
                        {suggestions.length > 0 ? (
                            suggestions.map((suggestion, index) => (
                                <div
                                    key={`suggestion-${index}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSuggestionSelect(suggestion);
                                    }}
                                    className={SearchBarStyle["suggestion-item"]}
                                >
                                    <div className={SearchBarStyle["suggestion-text"]}>
                                        {suggestion}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={SearchBarStyle["empty-state"]}>
                                <div className={SearchBarStyle["empty-state-text"]}>
                                    No farms found for "{query}"
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>,
            document.body
        );
    };

    return (
        <>
            <div ref={searchBarRef} className={`${SearchBarStyle["search-bar"]} ${className}`}>
                <form onSubmit={handleSearch} style={{ display: 'flex', width: '100%' }}>
                    {/* Mobile: always show back button on the left */}
                    {isMobile && (
                        <button
                            type="button"
                            onClick={handleBackInput}
                            className={SearchBarStyle["back-button"]}
                        >
                            <span className={SearchBarStyle["back-icon"]}>
                                <Back />
                            </span>
                        </button>
                    )}
                    
                    <div className={SearchBarStyle["search-container"]}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={query}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                            className={SearchBarStyle["search-input"]}
                            onFocus={handleInputFocus}
                        />
                    </div>
                    
                    {/* Mobile: only show clear button when typing, Desktop: always show search button */}
                    {isMobile ? (
                        // Mobile: only show clear button when typing
                        query.trim() && (
                            <button
                                type="button"
                                onClick={handleClearInput}
                                className={SearchBarStyle["clear-button"]}
                            >
                                <span className={SearchBarStyle["clear-icon"]}>
                                    <Close />
                                </span>
                            </button>
                        )
                    ) : (
                        // Desktop: always show search button
                        <button
                            type="submit"
                            className={SearchBarStyle["search-button"]}
                            aria-label="Search"
                        >
                            <span className={SearchBarStyle["search-icon"]}>
                                <SearchIcon />
                            </span>
                        </button>
                    )}
                </form>
            </div>
            
            <SuggestionsDropdown />
        </>
    );
};

export default SearchBar;
